import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import Button from '../ui/Button';
import FormField, { Input } from '../ui/FormField';
import StatusBanner from '../ui/StatusBanner';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 * Always shows success message to prevent email enumeration.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await authAPI.forgotPassword(email);
      setIsSubmitted(true);
    } catch (err: any) {
      // Rate limit error
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        // Always show success to prevent email enumeration
        setIsSubmitted(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white px-6 py-12">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-clinic-gold/10 rounded-full translate-y-1/2 translate-x-1/2" />

        <div className="relative w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-12 w-auto mx-auto"
            />
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-card shadow-clinic p-8 text-center">
            <div className="bg-clinic-primary/10 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <CheckCircleIcon className="w-8 h-8 text-clinic-primary" />
            </div>

            <h2 className="text-2xl font-bold text-clinic-dark font-body mb-4">
              Check Your Email
            </h2>

            <div className="bg-clinic-cream/50 rounded-card p-4 mb-6">
              <p className="text-clinic-dark">
                If an account exists with <strong className="text-clinic-primary">{email}</strong>, you will receive a password reset link shortly.
              </p>
            </div>

            <p className="text-sm text-clinic-muted mb-6">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                }}
                className="text-clinic-primary hover:underline font-medium"
              >
                try again
              </button>
              .
            </p>

            <Link
              to="/login"
              className="inline-flex items-center text-clinic-primary hover:underline font-medium"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Form State
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white px-6 py-12">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 -translate-x-1/2" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-clinic-gold/10 rounded-full translate-y-1/2 translate-x-1/2" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-12 w-auto mx-auto"
          />
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-card shadow-clinic p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-clinic-dark font-body">
              Forgot Your Password?
            </h2>
            <p className="mt-2 text-clinic-muted">
              Enter your email and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <StatusBanner
                variant="error"
                title="Unable to send reset link"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Email address" htmlFor="email">
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isSubmitting}
              disabled={!email}
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-clinic-primary hover:underline"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-clinic-muted">
            Don't have an account?{' '}
            <Link to="/register" className="text-clinic-primary hover:underline font-medium">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
